import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConsignmentInvoiceCandidates, useCreateConsignmentInvoice } from '../api/hooks';
import { describeError } from '../api/errors';
import type { ConsignmentInvoiceCandidate } from '../api/types';
import { chosenQuantity, pickableTotal, selectionTotals } from './consignment-invoice/model';
import { QuantityStepper } from './consignment-invoice/QuantityStepper';
import { Alert, Button, Empty, Field, Money, PageHeader, Panel, Spinner, Table } from '../ui';

/**
 * Create Consignment Invoice: one company, then the orders to group (PI-1). An order need not be fully ready: each
 * line offers what has been made and is not already invoiced, and the quantity to invoice is chosen per line.
 *
 * Tick the orders, set the quantities, press Create. The terms are worked on in the invoice that this creates. Selection is a set of order ids, so an order is in it once or
 * not at all, and no box is ever disabled: a mix of currencies is explained and holds Create
 * instead, because a box that goes dead for no visible reason reads as a broken page.
 */
export function CreateConsignmentInvoiceScreen() {
  const { data, isLoading, error } = useConsignmentInvoiceCandidates();
  const create = useCreateConsignmentInvoice();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const wanted = params.get('order');

  const [chosenCompany, setCompany] = useState<string | null>(null);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => (wanted ? new Set([wanted]) : new Set()));
  const [failed, setFailed] = useState<string | null>(null);
  /** The quantity chosen per order line; a line not here is invoiced at all that is ready. */
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const companies = useMemo(() => {
    const byId = new Map<string, string>();
    for (const o of data ?? []) byId.set(o.retailer.id, o.retailer.name);
    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  // An order opened from the order page names its company; otherwise the person chooses.
  const company = chosenCompany
    ?? (wanted ? data?.find((o) => o.orderId === wanted)?.retailer.id ?? null : null);

  const orders = useMemo(() => (data ?? []).filter((o) => o.retailer.id === company), [data, company]);
  const picked = orders.filter((o) => ticked.has(o.orderId));
  const currencies = [...new Set(picked.map((o) => o.currency))];
  const mixed = currencies.length > 1;
  const totals = useMemo(() => selectionTotals(orders, ticked, quantities), [orders, ticked, quantities]);

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;

  const tick = (id: string, on: boolean) => {
    setFailed(null);
    setTicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };

  async function submit() {
    if (picked.length === 0) {
      setFailed('Tick at least one order to include in the consignment invoice.');
      return;
    }
    if (mixed) return; // the warning above the button says why
    setFailed(null);
    try {
      const row = await create.mutateAsync({
        orderIds: picked.map((o) => o.orderId),
        // Every line of the ticked orders is named, so a line is invoiced only at the quantity shown for it.
        lines: picked.flatMap((o) => o.lines.map((l) => ({ orderLineId: l.orderLineId, quantity: chosenQuantity(l, quantities) }))),
      });
      void navigate(`/consignment-invoices/${row.id}`);
    } catch (e) {
      setFailed(describeError(e));
    }
  }

  return (
    <>
      <PageHeader
        title="Create consignment invoice"
        actions={<Button tone="ghost" onClick={() => void navigate('/consignment-invoices')}>Back to consignment invoices</Button>}
      />
      {failed ? <Alert tone="stop"><span className="mk-lines">{failed}</span></Alert> : null}

      <Panel title="Company" pad>
        <Field label="Company" hint="Only companies with something ready to invoice are listed.">
          <select
            className="mk-fld"
            value={company ?? ''}
            onChange={(e) => { setCompany(e.target.value || null); setTicked(new Set()); setFailed(null); }}
          >
            <option value="">Choose a company…</option>
            {companies.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </Field>
      </Panel>

      {companies.length === 0 ? (
        <Panel>
          <Empty title="Nothing ready to invoice" body="Pieces appear here once they are made and not already on a consignment invoice." />
        </Panel>
      ) : null}

      {company ? (
        <Panel title={`Ready to invoice · ${orders[0]?.retailer.name ?? ''}`}>
          <Table<ConsignmentInvoiceCandidate>
            className="mk-pi-orders"
            rows={orders}
            rowKey={(o) => o.orderId}
            rowClassName={(o) => (ticked.has(o.orderId) ? 'on' : undefined)}
            empty={<Empty title="Nothing to invoice" body="This company has nothing ready left to invoice." />}
            columns={[
              {
                header: '',
                width: '4%', center: true,
                render: (o) => (
                  // The label is the click target, so the box is easy to hit; no row handler exists to compete.
                  <label className="mk-pi-hit">
                    <input
                      type="checkbox"
                      className="mk-pi-check"
                      aria-label={`Include ${o.reference}`}
                      checked={ticked.has(o.orderId)}
                      onChange={(e) => tick(o.orderId, e.target.checked)}
                    />
                  </label>
                ),
              },
              { header: 'Order no.', width: '14%', center: true, render: (o) => <b>{o.reference}</b> },
              { header: 'Company', width: '20%', center: true, render: (o) => o.retailer.name },
              {
                header: 'Products', width: '22%', center: true,
                render: (o) => (
                  <div className="mk-pi-stack">
                    {o.lines.map((l) => <div key={l.orderLineId}>{l.description ?? '—'}</div>)}
                    {o.lines.length > 1 ? <div className="mk-dim mk-pi-total">Order total</div> : null}
                  </div>
                ),
              },
              {
                header: 'Qty', width: '26%', center: true,
                render: (o) => (
                  <div className="mk-pi-stack mk-pi-num">
                    {o.lines.map((l) => (
                      <div key={l.orderLineId}>
                        <QuantityStepper
                          value={chosenQuantity(l, quantities)}
                          max={l.quantity}
                          label={`${l.description ?? l.variantCode} on ${o.reference}`}
                          onChange={(q) => { setFailed(null); setQuantities((prev) => ({ ...prev, [l.orderLineId]: q })); }}
                        />
                        {l.ordered !== undefined && l.ready !== undefined ? (
                          <div className="mk-dim" style={{ fontSize: 11 }}>
                            {l.ready} of {l.ordered} ready{l.invoiced ? `, ${l.invoiced} already invoiced` : ''}
                          </div>
                        ) : null}
                      </div>
                    ))}
                    {o.lines.length > 1 ? <div className="mk-pi-total">&nbsp;</div> : null}
                  </div>
                ),
              },
              {
                header: 'Amount', width: '14%', center: true,
                // Each product's amount once. An order total is added only when there is more than one
                // product to add up; with one product it would just repeat that product's amount.
                render: (o) => (
                  <div className="mk-pi-stack mk-pi-num">
                    {o.lines.map((l) => (
                      <div key={l.orderLineId}>
                        <Money amount={pickableTotal({ orderId: o.orderId, lines: [l] }, quantities)} currency={o.currency} />
                      </div>
                    ))}
                    {o.lines.length > 1 ? (
                      <div className="mk-pi-total"><Money amount={pickableTotal(o, quantities)} currency={o.currency} /></div>
                    ) : null}
                  </div>
                ),
              },
            ]}
          />
          {mixed ? (
            <Alert tone="stop">
              The ticked orders are in different currencies ({currencies.join(', ')}). One consignment invoice uses one
              currency, so untick the orders in the other currency, or make a separate invoice for them.
            </Alert>
          ) : null}
          <div className="mk-pbody" style={{ display: 'flex', gap: 'var(--mk-space-4)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span>
              {totals.orders === 0
                ? 'No orders ticked'
                : `${totals.orders} order${totals.orders === 1 ? '' : 's'}, ${totals.products} product${totals.products === 1 ? '' : 's'}`}
            </span>
            {totals.orders > 0 && !mixed ? <b><Money amount={totals.amount} currency={currencies[0]} /></b> : null}
            <span className="mk-spacer" />
            <Button disabled={create.isPending || totals.orders === 0 || mixed} onClick={() => void submit()}>
              Create Consignment Invoice
            </Button>
          </div>
        </Panel>
      ) : null}
    </>
  );
}
